import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useLogo } from './LogoContext';
import serviceManager from '../services/serviceManager';
import { useMarketDataLoading } from '../hooks/useMarketDataLoading';

interface LoadingContextType {
  isLoading: boolean;
  isInitializing: boolean; // New: for unauthenticated users
  setIsLoading: (loading: boolean) => void;
  showLoader: () => void;
  hideLoader: () => void;
  loadingProgress: number;
  loadingMessage: string;
}

interface LoadingState {
  auth: boolean;
  logos: boolean;
  marketData: boolean;
  services: boolean;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

interface LoadingProviderProps {
  children: ReactNode;
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const { user, isLoading: authLoading } = useAuth();
  const { logosLoaded } = useLogo();
  const { isMarketDataLoaded, marketDataCount } = useMarketDataLoading();
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true); // For unauthenticated users
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [loadingState, setLoadingState] = useState<LoadingState>({
    auth: false,
    logos: false,
    marketData: false,
    services: false,
  });

  // Update loading progress based on completed states
  useEffect(() => {
    const completedStates = Object.values(loadingState).filter(Boolean).length;
    const totalStates = Object.keys(loadingState).length;
    const progress = (completedStates / totalStates) * 100;
    setLoadingProgress(progress);

    // Update loading message based on current state
    if (!loadingState.auth) {
      setLoadingMessage('Authenticating...');
    } else if (!loadingState.logos) {
      setLoadingMessage('Loading assets...');
    } else if (!loadingState.services) {
      setLoadingMessage('Starting services...');
    } else if (!loadingState.marketData) {
      setLoadingMessage(`Loading market data... (${marketDataCount} pairs)`);
    } else {
      setLoadingMessage('Almost ready...');
    }
  }, [loadingState, marketDataCount]);

  // Track authentication state
  useEffect(() => {
    if (!authLoading) {
      setLoadingState(prev => ({ ...prev, auth: true }));
    }
  }, [authLoading]);

  // Track logo loading state
  useEffect(() => {
    if (logosLoaded) {
      setLoadingState(prev => ({ ...prev, logos: true }));
    } else if (!user) {
      // If no user, mark logos as ready (they don't need to load)
      setLoadingState(prev => ({ ...prev, logos: true }));
    }
  }, [logosLoaded, user]);

  // Track services state
  useEffect(() => {
    if (user && serviceManager.areServicesRunning()) {
      setLoadingState(prev => ({ ...prev, services: true }));
    } else if (!user) {
      // If no user, mark services as ready (they don't need to run)
      setLoadingState(prev => ({ ...prev, services: true }));
    }
  }, [user]);

  // Track market data loading
  useEffect(() => {
    if (isMarketDataLoaded) {
      setLoadingState(prev => ({ ...prev, marketData: true }));
    } else if (!user) {
      // If no user, mark market data as ready (it doesn't need to load)
      setLoadingState(prev => ({ ...prev, marketData: true }));
    }
  }, [isMarketDataLoaded, user]);

  // Determine if we should show the loader
  useEffect(() => {
    // For unauthenticated users, show initialization loader until everything is ready
    if (!user && !authLoading) {
      // Check if basic initialization is complete (auth + logos)
      const isBasicInitComplete = loadingState.auth && loadingState.logos;
      setIsInitializing(!isBasicInitComplete);
      setIsLoading(false); // Don't show main loader for unauthenticated users
      return;
    }

    // For authenticated users, show main loader while services are starting
    const shouldShowLoader = Boolean(authLoading || 
      (user && (!loadingState.auth || 
       !loadingState.logos || 
       !loadingState.services || 
       !loadingState.marketData)));

    setIsLoading(shouldShowLoader);
    setIsInitializing(false); // Don't show initialization loader for authenticated users
  }, [authLoading, loadingState, user]);



  const showLoader = () => setIsLoading(true);
  const hideLoader = () => setIsLoading(false);

  const value: LoadingContextType = {
    isLoading,
    isInitializing,
    setIsLoading,
    showLoader,
    hideLoader,
    loadingProgress,
    loadingMessage,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};
